import { createContext, useContext, type Context as ReactContext } from 'react';

import { useCrossProductUrlWrapper } from '@atlassiansox/analytics-cross-product-interaction-client';

import { fg } from '@atlaskit/platform-feature-flags';

import type { AnalyticsAttributes } from './analytics';
import { getSiteAri } from './utils/getSiteAri';

export type ProjectsGoalsContextProps = {
	containerId: string;
	parentProduct?: 'confluence' | 'jira' | 'focus' | 'teams' | 'home';
	currentUser?:
		| undefined
		| {
				id: string;
				name: string;
				avatarUrl?: string;
				accountId?: string;
		  };
	orgId?: string;
	analyticsAttributes: AnalyticsAttributes;
	isEditable?: boolean;
	isOwnerOrContributor?: boolean;
	parentCloudId?: string;
};

export const ProjectsGoalsContext: ReactContext<ProjectsGoalsContextProps> =
	createContext<ProjectsGoalsContextProps>({
		containerId: '',
		orgId: '',
		currentUser: undefined,
		analyticsAttributes: {},
		isEditable: undefined,
		isOwnerOrContributor: undefined,
		parentCloudId: undefined,
		parentProduct: undefined,
	});

export const useProjectsGoals = (): {
	containerId: string;
	orgId: string | undefined;
	parentProduct?: string;
	currentUser:
		| {
				id: string;
				name: string;
				avatarUrl?: string;
				accountId?: string;
		  }
		| undefined;
} => {
	const context = useContext(ProjectsGoalsContext);

	return {
		containerId: context.containerId,
		orgId: context.orgId,
		currentUser: context.currentUser,
		parentProduct: context.parentProduct,
	};
};

export const useGetCrossProductWrappedUrl = (
	bridge: string,
	url: string,
	app: 'goals' | 'projects',
	destinationApp: 'goals' | 'projects' | 'teams' | 'focus',
	featureFlag: 'projects_goals' | 'teams' = 'projects_goals',
): string => {
	const enableCrossMAUUsage =
		featureFlag === 'teams'
			? fg('townsquare_teams_cross_app_mau')
			: fg('platform_preview_panels_cross_app_interaction');

	const buildUrlWithAnalyticsParams = useCrossProductUrlWrapper({
		bridge,
		product: 'townsquare',
		subProduct: app,
	});

	return enableCrossMAUUsage && app !== destinationApp ? buildUrlWithAnalyticsParams(url) : url;
};

export const useProjectsGoalsContainerId = (): string => {
	const context = useContext(ProjectsGoalsContext);

	return context.containerId;
};

export const useProjectsGoalsOrgId = (): string | undefined => {
	const context = useContext(ProjectsGoalsContext);

	return context.orgId;
};

export const useProjectsGoalsSiteAri = (): string => {
	const context = useContext(ProjectsGoalsContext);

	return getSiteAri(context.containerId);
};

export const useAnalyticsAttributes = (): AnalyticsAttributes => {
	const context = useContext(ProjectsGoalsContext);

	return context.analyticsAttributes;
};

export const useIsProjectsGoalsEditable = (): boolean => {
	const context = useContext(ProjectsGoalsContext);

	// Default to editable as this is the most common case
	return context.isEditable === undefined ? true : context.isEditable;
};

export const useIsProjectsGoalsOwnerOrContributor = (): boolean => {
	const context = useContext(ProjectsGoalsContext);

	return !!context.isOwnerOrContributor;
};

export const useIsSameSite = (): boolean => {
	const context = useContext(ProjectsGoalsContext);

	return context.containerId === context.parentCloudId;
};

export type Context = {
	environment?: 'prod' | 'staging' | 'local';
	objectAri: string;
	entity: 'project' | 'goal';
};

export type Environment = 'prod' | 'staging';

export const getEnvironment = (): Environment => {
	const jiraEnv = document.querySelector<HTMLMetaElement>('meta[name=ajs-environment]')?.content;
	if (jiraEnv === 'staging') {
		return 'staging';
	}

	const townsquareEnv = document.querySelector<HTMLMetaElement>('meta[name=env]')?.content;
	if (townsquareEnv?.startsWith('stg')) {
		return 'staging';
	}

	return 'prod';
};
